"use client";

import { useState } from "react";
import { submitFeedback } from "@/lib/api/feedback";
import type { FeedbackRequest } from "@/lib/api/feedback";
import { canShowPrompt, recordPromptShown } from "@/lib/feedback/promptCeiling";

/**
 * Reusable feedback widget for web — mirrors the mobile RatingPrompt API.
 *
 * Modes:
 *   "thumbs"       — thumbs-up / thumbs-down (inline SVG icons, no emojis)
 *   "stars"        — 1–5 star picker
 *   "thumbs+text"  — thumbs + optional free-text
 *   "intent-chips" — chip-select (pass `chips` prop)
 *
 * Dedup: when `dedupeKey` is set, localStorage prevents re-submission on
 * the same browser session.
 *
 * Type-safety note: `source` is cast because generated api-client types
 * don't yet include the Tranche-2 sources. Backend VALID_SOURCES is the
 * real gate.
 *
 * Prompt ceiling: `canShowPrompt(source)` is checked on mount via a
 * useEffect gate. If the session's modal ceiling is already consumed or a
 * cooldown is active, the component renders nothing. After successful
 * submission, `recordPromptShown(source)` updates localStorage/
 * sessionStorage so future loads respect the ceiling.
 */

export interface RatingChip {
  id: string;
  label: string;
}

export interface RatingPromptProps {
  source: string;
  context?: Record<string, unknown>;
  mode: "thumbs" | "stars" | "thumbs+text" | "stars+text" | "intent-chips";
  prompt?: string;
  chips?: RatingChip[];
  dedupeKey?: string;
  onSubmit?: () => void;
}

const DEDUPE_NS = "feedback_submitted";

function isAlreadySubmitted(key: string): boolean {
  try {
    return localStorage.getItem(`${DEDUPE_NS}:${key}`) === "true";
  } catch {
    return false;
  }
}

function markSubmitted(key: string): void {
  try {
    localStorage.setItem(`${DEDUPE_NS}:${key}`, "true");
  } catch {
    // Ignore localStorage errors (private browsing, etc.)
  }
}

function feedbackSource(source: string): FeedbackRequest["source"] {
  return source as FeedbackRequest["source"];
}

export default function RatingPrompt({
  source,
  context,
  mode,
  prompt,
  chips,
  dedupeKey,
  onSubmit,
}: RatingPromptProps) {
  const [thumbs, setThumbs] = useState<"up" | "down" | null>(null);
  const [stars, setStars] = useState(0);
  const [hoveredStar, setHoveredStar] = useState(0);
  const [chipId, setChipId] = useState<string | null>(null);
  const [text, setText] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [alreadyDone] = useState(() => (dedupeKey ? isAlreadySubmitted(dedupeKey) : false));
  const [ceilingBlocked] = useState(() => !canShowPrompt(source));

  async function doSubmit(payload: {
    rating?: number;
    text?: string;
    extraContext?: Record<string, unknown>;
  }) {
    try {
      await submitFeedback({
        source: feedbackSource(source),
        rating: payload.rating,
        text: payload.text,
        context: { ...context, ...payload.extraContext },
      });
      if (dedupeKey) markSubmitted(dedupeKey);
      // Record against the ceiling after successful submission.
      recordPromptShown(source);
      setSubmitted(true);
      onSubmit?.();
    } catch {
      // Soft-fail — feedback errors must never disrupt the chat UX
    }
  }

  if (ceilingBlocked || submitted || alreadyDone) {
    if (ceilingBlocked) return null;
    return <span style={{ fontSize: 11, color: "var(--ish-text-faint)" }}>Thank you</span>;
  }

  function handleThumb(value: "up" | "down") {
    setThumbs(value);
    if (mode === "thumbs") {
      void doSubmit({ rating: value === "up" ? 1 : 0 });
    }
  }

  function handleStars(n: number) {
    setStars(n);
    if (mode === "stars") {
      void doSubmit({ rating: n });
    }
    // In "stars+text" mode, wait for the Submit button press.
  }

  function handleSubmitWithStarsText() {
    void doSubmit({
      rating: stars || undefined,
      text: text.trim() || undefined,
    });
  }

  function handleChip(id: string) {
    setChipId(id);
    void doSubmit({ text: id, extraContext: { chip_id: id } });
  }

  function handleSubmitWithText() {
    void doSubmit({
      rating: thumbs === "up" ? 1 : 0,
      text: text.trim() || undefined,
    });
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {prompt && (
        <p style={{ margin: 0, fontSize: 11, color: "var(--ish-text-muted)" }}>{prompt}</p>
      )}

      <WebRatingControls
        mode={mode}
        chips={chips}
        thumbs={thumbs}
        stars={stars}
        hoveredStar={hoveredStar}
        chipId={chipId}
        text={text}
        onTextChange={setText}
        onThumb={handleThumb}
        onTextSubmit={handleSubmitWithText}
        onStarsTextSubmit={handleSubmitWithStarsText}
        onStars={handleStars}
        onStarHover={setHoveredStar}
        onChip={handleChip}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Internal: thumb button (SVG-based, no emoji)
// ---------------------------------------------------------------------------

interface WebRatingControlsProps {
  mode: RatingPromptProps["mode"];
  chips?: RatingChip[];
  thumbs: "up" | "down" | null;
  stars: number;
  hoveredStar: number;
  chipId: string | null;
  text: string;
  onTextChange: (value: string) => void;
  onThumb: (value: "up" | "down") => void;
  onTextSubmit: () => void;
  onStarsTextSubmit: () => void;
  onStars: (value: number) => void;
  onStarHover: (value: number) => void;
  onChip: (value: string) => void;
}

function WebRatingControls({
  mode,
  chips,
  thumbs,
  stars,
  hoveredStar,
  chipId,
  text,
  onTextChange,
  onThumb,
  onTextSubmit,
  onStarsTextSubmit,
  onStars,
  onStarHover,
  onChip,
}: WebRatingControlsProps) {
  return (
    <>
      {(mode === "thumbs" || mode === "thumbs+text") && (
        <WebThumbsRow thumbs={thumbs} onPress={onThumb} />
      )}
      {mode === "thumbs+text" && thumbs !== null && (
        <WebFeedbackTextBox text={text} onTextChange={onTextChange} onSubmit={onTextSubmit} />
      )}
      {(mode === "stars" || mode === "stars+text") && (
        <WebStarsRow
          stars={stars}
          hoveredStar={hoveredStar}
          onPress={onStars}
          onHover={onStarHover}
        />
      )}
      {mode === "stars+text" && stars > 0 && (
        <WebFeedbackTextBox text={text} onTextChange={onTextChange} onSubmit={onStarsTextSubmit} />
      )}
      {mode === "intent-chips" && chips && (
        <WebChipRow chips={chips} activeChipId={chipId} onPress={onChip} />
      )}
    </>
  );
}

interface WebThumbsRowProps {
  thumbs: "up" | "down" | null;
  onPress: (value: "up" | "down") => void;
}

function WebThumbsRow({ thumbs, onPress }: WebThumbsRowProps) {
  return (
    <div style={{ display: "flex", gap: 4 }}>
      <WebThumbBtn kind="up" active={thumbs === "up"} onPress={() => onPress("up")} />
      <WebThumbBtn kind="down" active={thumbs === "down"} onPress={() => onPress("down")} />
    </div>
  );
}

interface WebFeedbackTextBoxProps {
  text: string;
  onTextChange: (value: string) => void;
  onSubmit: () => void;
}

function WebFeedbackTextBox({ text, onTextChange, onSubmit }: WebFeedbackTextBoxProps) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 4 }}>
      <textarea
        value={text}
        onChange={(e) => onTextChange(e.target.value)}
        placeholder="Tell us more (optional)"
        rows={2}
        style={{
          fontSize: 11,
          padding: "4px 6px",
          borderRadius: 6,
          border: "1px solid var(--ish-border)",
          background: "var(--color-bg)",
          color: "var(--color-dark)",
          resize: "none",
        }}
      />
      <button
        type="button"
        onClick={onSubmit}
        style={{
          fontSize: 11,
          padding: "4px 10px",
          borderRadius: 6,
          border: "none",
          background: "var(--ish-accent)",
          color: "#fff",
          cursor: "pointer",
          alignSelf: "flex-start",
        }}
      >
        Submit
      </button>
    </div>
  );
}

interface WebStarsRowProps {
  stars: number;
  hoveredStar: number;
  onPress: (value: number) => void;
  onHover: (value: number) => void;
}

function WebStarsRow({ stars, hoveredStar, onPress, onHover }: WebStarsRowProps) {
  return (
    <div style={{ display: "flex", gap: 2 }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onPress(n)}
          onMouseEnter={() => onHover(n)}
          onMouseLeave={() => onHover(0)}
          aria-label={`${n} star`}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: 2,
            fontSize: 14,
            color: n <= (hoveredStar || stars) ? "var(--ish-gold, #ffde59)" : "var(--ish-border)",
          }}
        >
          {"★"}
        </button>
      ))}
    </div>
  );
}

interface WebChipRowProps {
  chips: RatingChip[];
  activeChipId: string | null;
  onPress: (value: string) => void;
}

function WebChipRow({ chips, activeChipId, onPress }: WebChipRowProps) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
      {chips.map((chip) => (
        <WebChipButton
          key={chip.id}
          chip={chip}
          active={activeChipId === chip.id}
          onPress={() => onPress(chip.id)}
        />
      ))}
    </div>
  );
}

interface WebChipButtonProps {
  chip: RatingChip;
  active: boolean;
  onPress: () => void;
}

function WebChipButton({ chip, active, onPress }: WebChipButtonProps) {
  return (
    <button
      type="button"
      onClick={onPress}
      aria-pressed={active}
      style={{
        padding: "4px 10px",
        borderRadius: 99,
        border: `1px solid ${active ? "var(--ish-accent)" : "var(--ish-border)"}`,
        background: active ? "var(--ish-accent)" : "transparent",
        color: active ? "#fff" : "var(--color-dark)",
        fontSize: 11,
        fontWeight: 500,
        cursor: "pointer",
      }}
    >
      {chip.label}
    </button>
  );
}

interface WebThumbBtnProps {
  kind: "up" | "down";
  active: boolean;
  onPress: () => void;
}

function WebThumbBtn({ kind, active, onPress }: WebThumbBtnProps) {
  const label = kind === "up" ? "Helpful" : "Not helpful";
  const color = active ? "var(--ish-accent)" : "var(--ish-text-faint)";
  return (
    <button
      type="button"
      onClick={onPress}
      aria-label={label}
      aria-pressed={active}
      style={{
        background: "none",
        border: "none",
        cursor: "pointer",
        padding: 3,
        color,
        display: "flex",
        alignItems: "center",
      }}
      title={label}
    >
      {kind === "up" ? (
        <svg width="14" height="14" viewBox="0 0 256 256" fill="currentColor">
          <path d="M234,80.12A24,24,0,0,0,216,72H160V56a40,40,0,0,0-40-40,8,8,0,0,0-7.16,4.42L75.06,96H32a16,16,0,0,0-16,16v88a16,16,0,0,0,16,16H204a24,24,0,0,0,23.82-21l12-96A24,24,0,0,0,234,80.12ZM32,112H72v88H32Zm183.71,9.14-12,96A8,8,0,0,1,196,224H88V105.89l36.71-73.43A24,24,0,0,1,144,56V80a8,8,0,0,0,8,8h64a8,8,0,0,1,7.92,9.14Z" />
        </svg>
      ) : (
        <svg
          width="14"
          height="14"
          viewBox="0 0 256 256"
          fill="currentColor"
          style={{ transform: "rotate(180deg)" }}
        >
          <path d="M234,80.12A24,24,0,0,0,216,72H160V56a40,40,0,0,0-40-40,8,8,0,0,0-7.16,4.42L75.06,96H32a16,16,0,0,0-16,16v88a16,16,0,0,0,16,16H204a24,24,0,0,0,23.82-21l12-96A24,24,0,0,0,234,80.12ZM32,112H72v88H32Zm183.71,9.14-12,96A8,8,0,0,1,196,224H88V105.89l36.71-73.43A24,24,0,0,1,144,56V80a8,8,0,0,0,8,8h64a8,8,0,0,1,7.92,9.14Z" />
        </svg>
      )}
    </button>
  );
}
